/* eslint-disable no-await-in-loop */
// Parts are signed → uploaded → collected strictly in sequence (each depends on the previous
// step + retry), so awaiting inside the loop is intentional here.
import { nanoid } from "nanoid";
import { s3MultipartAbort, s3MultipartComplete, s3MultipartCreate, s3MultipartSignPart } from "../apis";

/**
 * Uploads a job-form asset (file / recorded video) to S3 via core-api's multipart endpoints and
 * resolves to the final stored URL — the value saved as the question's answer.
 *
 * Mirrors lms-frontend's chunk uploader: create → sign each 5 MB part → PUT directly to S3 →
 * complete (abort on failure). Part uploads retry up to 3×.
 */
const MIN_CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB (S3 multipart minimum part size)
const MAX_RETRIES = 3;

// core-api wraps successful responses as { data: <payload> }; unwrap defensively.
const unwrap = <T>(envelope: unknown): T => {
  const env = envelope as { data?: T };
  return env && env.data !== undefined ? (env.data as T) : (envelope as T);
};

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const buildFormAssetKey = (kind: "video" | "files", formId: string | undefined, questionId: string | undefined, filename: string): string => {
  const ext = (filename.split(".").pop() || "bin").toLowerCase();
  return `jobSurvey/${kind}/form/${formId || "unknown"}/${questionId || "unknown"}/${nanoid()}.${ext}`;
};

/**
 * Geo captures get their own prefix rather than riding on `jobSurvey/`: S3 keys are permanent, and
 * nothing about an internship photo belongs under a job-survey path. core-api's upload prefix
 * allow-list must contain `geoCapture/` or every capture is rejected at create-multipart.
 */
export const buildGeoCaptureAssetKey = (formId: string | undefined, questionId: string | undefined, filename: string): string => {
  const ext = (filename.split(".").pop() || "jpg").toLowerCase();
  return `geoCapture/form/${formId || "unknown"}/${questionId || "unknown"}/${nanoid()}.${ext}`;
};

/**
 * @param onProgress optional callback with overall upload percent (0–100), reported per completed part.
 */
export const uploadToS3 = async (blob: Blob, key: string, contentType?: string, onProgress?: (percent: number) => void): Promise<string> => {
  const type = contentType || blob.type || "application/octet-stream";
  const { uploadId } = unwrap<{ uploadId: string }>(await s3MultipartCreate({ key, contentType: type }));

  try {
    const parts: { ETag: string; PartNumber: number }[] = [];
    const totalParts = Math.max(1, Math.ceil(blob.size / MIN_CHUNK_SIZE));

    for (let i = 0; i < totalParts; i++) {
      const partNumber = i + 1;
      const chunk = blob.slice(i * MIN_CHUNK_SIZE, (i + 1) * MIN_CHUNK_SIZE);
      const { url } = unwrap<{ url: string }>(await s3MultipartSignPart({ key, uploadId, partNumber }));

      let etag: string | null = null;
      let attempt = 0;
      while (attempt < MAX_RETRIES && !etag) {
        attempt += 1;
        try {
          const putRes = await fetch(url, { method: "PUT", body: chunk, headers: { "Content-Type": type } });
          if (!putRes.ok) throw new Error(`Part ${partNumber} upload failed (${putRes.status})`);
          etag = putRes.headers.get("ETag");
          if (!etag) throw new Error(`Part ${partNumber} missing ETag (check bucket CORS Access-Control-Expose-Headers)`);
        } catch (err) {
          if (attempt >= MAX_RETRIES) throw err;
          await wait(1000);
        }
      }

      parts.push({ ETag: etag as string, PartNumber: partNumber });
      // Progress by parts completed (reserve the last step for `complete`).
      if (onProgress) onProgress(Math.round((partNumber / (totalParts + 1)) * 100));
    }

    const { videoUrl } = unwrap<{ videoUrl: string }>(await s3MultipartComplete({ key, uploadId, parts }));
    onProgress?.(100);
    return videoUrl;
  } catch (err) {
    try {
      await s3MultipartAbort({ key, uploadId });
    } catch {
      // best-effort cleanup; surface the original error below
    }
    throw err;
  }
};
